import React from 'react';

import FormDialog from 'scenes/Dialog/components/FormDialog';
import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import ConfirmDialog from 'scenes/Dialog/components/ConfirmDialog';
import UploadResumeForm from 'scenes/Form/components/UploadResumeForm';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import ProfileRequestRecommendationForm from 'scenes/Form/components/ProfileRequestRecommendationForm';

import { useApi } from 'hooks/useApi';
import { POPUP_NOTIFICATION_TYPE } from 'constants/popups';
import { ROUTE } from 'constants/route';
import { ENDPOINT } from 'constants/endpoints';
import { Option } from 'helpers/user';

const withPopupNotification = WrappedComponent => props => {
  const { history } = props;
  const { post } = useApi();

  const postRequest = (params, callback = null) => {
    post(ENDPOINT.ACTION.ONE_TIME_POPUP_SAVE, params, {
      handleError: response => ResponseErrorDialog({ response }),
      handleSuccess: ({ popups }) => {
        props.globalActions.updateUserPopups(popups);
        if (callback) {
          callback();
        }
      },
    });
  };

  const showDialog = (
    bodyRender,
    textSuccessBtn,
    textCancelBtn,
    type,
    callback
  ) =>
    ConfirmDialog({
      bodyRender,
      textSuccessBtn,
      textCancelBtn,
      modeless: false,
      dismissWhenClose: false,
      hasCloseButton: false,
    }).then(
      () => postRequest({ type, accept: true }, callback),
      () => postRequest({ type, accept: false })
    );

  const showGatPopup = (type, callback) =>
    showDialog(
      <p>
        Do you want to take the GAT Test? Doing so, will increase your
        visibility to hundreds of employers
      </p>,
      'Yes',
      'No Thanks',
      type,
      callback
    );

  const showGatWarning = () =>
    ConfirmDialog({
      popupStyles: {
        maxWidth: '642px',
        padding: '35px 35px 45px 35px',
      },
      bodyRender: (
        <p
          style={{
            textAlign: 'left',
          }}
        >
          This test is one of many data points firms use to base their interview
          decisions. It is not worth it to cheat and receiving aid on this
          examination or distributing our copyrighted material is in direct
          violation of our terms of use.
        </p>
      ),
      textSuccessBtn: 'Start Test',
      textCancelBtn: 'No Thanks',
      dismissWhenClose: true,
    }).then(() =>
      history.push(ROUTE.CANDIDATE_APTITUDE_TEST, { confirmed: true })
    );

  const showInterviewDataPopup = (type, callback) =>
    showDialog(
      <p>
        Do you want to tell us about yourself? We are looking forward to
        learning more about you.
      </p>,
      'Yes',
      'No Thanks',
      type,
      callback
    );

  const showReviewPopup = (type, callback) =>
    showDialog(
      <p>
        Would you like to write a review? Doing so will grant you free access to
        read all reviews for 12 months.
      </p>,
      'Add Review',
      'No Thanks',
      type,
      callback
    );

  const showRecommendationPopup = (type, callback) =>
    showDialog(
      <p>
        Do you want to add a recommendation? Doing so, will increase your
        visibility to hundreds of employers.
      </p>,
      'Add Recommendation',
      'No Thanks',
      type,
      callback
    );

  const showAddRecommendationDialig = () =>
    FormDialog({
      title: 'Ask for Recommendation',
      modeless: false,
      compact: true,
      form: (actions, params) => (
        <ProfileRequestRecommendationForm actions={actions} params={params} />
      ),
    }).then(
      () =>
        BasicDialog({
          bodyRender:
            'Your recommendation request has been sent to the intended recipient.',
        }),
      response => ResponseErrorDialog({ response })
    );

  const showOldResumePopup = (type, callback) =>
    showDialog(
      <p>
        Would you like to update your resume? Doing so, will make sure your
        latest resume is sent to employers when you apply for positions
      </p>,
      'Yes',
      'No Thanks',
      type,
      callback
    );

  const showNetworkSchoolAlumniPopup = (type, callback) =>
    showDialog(
      <p>Add firm and school alumni to your network?</p>,
      'Yes',
      'No Thanks',
      type,
      callback
    );

  const open = (type, callback) => {
    if (type === POPUP_NOTIFICATION_TYPE.HOME_INTERVIEW_DATA) {
      showInterviewDataPopup(type, () =>
        props.history.push(
          ROUTE.CANDIDATE_PROFILE + '?action=updateInterviewData'
        )
      );
    }

    if (type === POPUP_NOTIFICATION_TYPE.HOME_GAT) {
      showGatPopup(type, () => {
        showGatWarning().then(() => {
          if (callback && typeof callback === 'function') {
            callback();
          }
        });
      });
    }

    if (type === POPUP_NOTIFICATION_TYPE.HOME_RECOMMENDATION) {
      showRecommendationPopup(type, () => {
        showAddRecommendationDialig();

        if (callback && typeof callback === 'function') {
          callback();
        }
      });
    }

    if (type === POPUP_NOTIFICATION_TYPE.HOME_RESUME_OLD) {
      showOldResumePopup(type, () => {
        FormDialog({
          modeless: false,
          compact: false,
          popupStyles: {
            paddingBottom: '30px',
          },
          dismissWhenClose: true,
          form: (actions, params) => (
            <UploadResumeForm actions={actions} params={params} />
          ),
        }).then(() => {
          BasicDialog({
            bodyRender: (
              <p className="dialog__message">
                Resume was successfully uploaded.
              </p>
            ),
          });

          if (!Option.hasResume(props.user.options)) {
            props.globalActions.updateUserResume();
          }
        });
        if (callback && typeof callback === 'function') {
          callback();
        }
      });
    }

    if (type === POPUP_NOTIFICATION_TYPE.HOME_REVIEW) {
      showReviewPopup(type, () =>
        props.history.push(ROUTE.ADD_REVIEW_EMPLOYEE)
      );
    }

    if (type === POPUP_NOTIFICATION_TYPE.NETWORK_SCHOOL_ALUMNI) {
      showNetworkSchoolAlumniPopup(type, () => {
        if (callback && typeof callback === 'function') {
          callback();
        }
      });
    }
  };

  return <WrappedComponent {...props} oneTimePopup={{ open }} />;
};

export default withPopupNotification;
